import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import type { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { LKDesktopNotificationSettings } from '../firebase/firestoreQuery';
import { getLKDesktopNotificationSettings } from '../firebase/firestoreQuery';
import { isDesktopVersionLowerThan } from '../services/desktopHelpers';
import {
  mouseDownListenerForDrag,
  mouseMoveListenerForDrag,
  mouseUpListenerForDrag,
} from '../services/desktopIntegration';
import { Z_INDICES } from '../styles';
import { getIsMac } from '../utils/electron';
import AppBar from './AppBar';

const DISMISSED_KEY = 'lk-desktop-notification-dismissed';
const MAX_TIMEOUT_MS = 2_147_483_647;
const DOWNLOAD_URL = 'https://ava.me/captions';

interface Props {
  children: React.ReactNode;
}

export const getDesktopSunsetState = (
  settings: LKDesktopNotificationSettings,
  currentTime: number,
  dismissed: boolean
): 'banner' | 'disabled' | null => {
  if (currentTime >= settings.sunsetDate.getTime()) return 'disabled';
  if (!dismissed && currentTime >= settings.notificationDate.getTime()) return 'banner';
  return null;
};

const useStyles = makeStyles((theme: Theme) => ({
  appBar: {
    background: 'transparent',
    boxShadow: 'none',
    zIndex: theme.zIndex.snackbar,
  },
  banner: {
    borderRadius: 0,
    width: '100%',
    WebkitAppRegion: 'no-drag',
    '& .MuiAlert-message': {
      flex: 1,
      textAlign: 'center',
    },
  },
  disabledView: {
    position: 'fixed',
    top: window.isElectron ? (getIsMac() ? 28 : 32) : 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: Z_INDICES.desktopSunsetDisabledView,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    textAlign: 'center',
    WebkitAppRegion: 'no-drag',
  },
  disabledContent: {
    maxWidth: 640,
  },
  disabledIcon: {
    marginBottom: 24,
    color: theme.palette.error.main,
    fontSize: 64,
  },
  disabledTitle: {
    marginBottom: 16,
  },
  downloadButton: {
    marginTop: 24,
  },
}));

const DesktopSunsetBanner = ({ children }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [settings, setSettings] = useState<LKDesktopNotificationSettings | null>(null);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [dismissed, setDismissed] = useState(() => Boolean(sessionStorage.getItem(DISMISSED_KEY)));

  useEffect(() => {
    if (!window.__TAURI__) return;

    let active = true;

    void getLKDesktopNotificationSettings()
      .then(async (notificationSettings) => {
        if (!notificationSettings) return;

        const isOutdated = await isDesktopVersionLowerThan(notificationSettings.version);
        if (active && isOutdated) {
          setSettings(notificationSettings);
        }
      })
      .catch((error) => console.error('Failed to load the desktop sunset notification:', error));

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!settings) return;

    const nextDate =
      currentTime < settings.notificationDate.getTime() ? settings.notificationDate : settings.sunsetDate;
    const delay = nextDate.getTime() - currentTime;
    if (delay <= 0) return;

    const timeout = window.setTimeout(() => setCurrentTime(Date.now()), Math.min(delay, MAX_TIMEOUT_MS));
    return () => window.clearTimeout(timeout);
  }, [currentTime, settings]);

  if (!settings) return <>{children}</>;

  const state = getDesktopSunsetState(settings, currentTime, dismissed);

  if (state === 'disabled') {
    return (
      <div
        className={classes.disabledView}
        role="alert"
        onMouseDown={mouseDownListenerForDrag}
        onMouseMove={mouseMoveListenerForDrag}
        onMouseUp={mouseUpListenerForDrag}
      >
        <div className={classes.disabledContent}>
          <WarningAmberIcon className={classes.disabledIcon} />
          <Typography className={classes.disabledTitle} variant="h4">
            {t<string>('desktopSunset.disabledTitle')}
          </Typography>
          <Typography variant="body1">
            {t<string>('desktopSunset.disabledMessage', {
              sunsetDate: settings.sunsetDate,
              version: settings.version,
            })}
          </Typography>
          <Button
            className={classes.downloadButton}
            component="a"
            href={DOWNLOAD_URL}
            variant="contained"
            onClick={(event) => {
              if (!window.desktopIntegration) return;
              event.preventDefault();
              window.desktopIntegration.openExternalURL(DOWNLOAD_URL);
            }}
          >
            {t<string>('desktopSunset.download')}
          </Button>
        </div>
      </div>
    );
  }

  if (state !== 'banner') return <>{children}</>;

  return (
    <>
      <AppBar className={classes.appBar} position="fixed">
        <Alert
          className={classes.banner}
          severity="warning"
          variant="filled"
          action={
            <IconButton
              aria-label={t<string>('desktopSunset.dismiss')}
              color="inherit"
              size="small"
              onClick={() => {
                sessionStorage.setItem(DISMISSED_KEY, 'true');
                setDismissed(true);
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {t<string>('desktopSunset.message', { sunsetDate: settings.sunsetDate })}
        </Alert>
      </AppBar>
      {children}
    </>
  );
};

export default DesktopSunsetBanner;
